/**
 * Watchdog no painel (decisões 187 a 194 e 244 a 248; conhecimento/analises/ANALISE-SENTINELA.md).
 *
 * - Identificador do computador: criado uma vez neste navegador e guardado no `localStorage` do
 *   painel. Não passa pelo `chrome.storage.sync` nem pelo cookie de domínio, então o Chrome Sync
 *   não leva o mesmo identificador para outro computador (era a falha do Sentinela).
 * - Token de sessão: emitido pelo servidor quando o computador é autorizado, guardado por conta.
 * - Os dois vão só nas rotas `api/v2/` (cabeçalhos `watchdog-device` e `watchdog-token`).
 * - O pedido de acesso leva o fuso e os idiomas deste navegador: a data do email de autorização
 *   sai no fuso e no formato de quem pediu (decisões 201 e 202).
 * - A abertura e o pedido levam os detalhes do computador (decisão 247): versão do navegador e do
 *   sistema e tamanho da tela, para a pessoa reconhecer cada um em Computadores.
 */
import { readAuth } from './auth';
import { browserLanguages } from './format';

const DEVICE_KEY = 'lm.watchdog.device';
const TOKEN_PREFIX = 'lm.watchdog.token.';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function randomId(): string {
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Fuso e idiomas deste navegador, para o email de autorização (decisão 202). */
export function deviceClock(): { timeZone?: string; languages: string[] } {
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    timeZone = undefined;
  }
  return { timeZone, languages: browserLanguages().slice(0, 5) };
}

export interface ClientDetails {
  browserVersion?: string;
  osVersion?: string;
  screen?: { width: number; height: number };
}

interface UserAgentData {
  brands?: { brand: string; version: string }[];
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>;
}

/** Espera máxima pela versão do sistema (Client Hints): a abertura não fica presa nela. */
const DETAILS_WAIT_MS = 500;

/**
 * Versão do sistema para a pessoa ler: "15" no macOS (ou "10.15"), "11" ou "10" no Windows. No
 * Windows, a versão 13 ou mais da plataforma é o Windows 11. Nos outros sistemas, nada.
 */
export function readableOsVersion(platform: string, platformVersion: string): string | undefined {
  const parts = platformVersion.split('.').map((part) => Number(part));
  const major = parts[0];
  if (major === undefined || !Number.isFinite(major)) return undefined;
  if (/mac/i.test(platform)) {
    if (major === 10 && Number.isFinite(parts[1])) return `10.${parts[1]}`;
    return major > 0 ? String(major) : undefined;
  }
  if (/windows/i.test(platform)) {
    if (major >= 13) return '11';
    return major > 0 ? '10' : undefined;
  }
  return undefined;
}

async function readDetails(): Promise<ClientDetails> {
  const details: ClientDetails = {};
  if (typeof screen !== 'undefined' && screen.width > 0 && screen.height > 0) {
    details.screen = { width: Math.round(screen.width), height: Math.round(screen.height) };
  }
  // Chrome e Edge. Safari e Firefox não têm: a versão do navegador sai do user agent no servidor.
  const ua =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData
      : undefined;
  if (!ua) return details;
  const brands = (ua.brands ?? []).filter((item) => !/not.?a.?brand/i.test(item.brand));
  const brand = brands.find((item) => item.brand !== 'Chromium') ?? brands[0];
  if (brand && /^\d+$/.test(brand.version)) details.browserVersion = brand.version;
  if (ua.platform && ua.getHighEntropyValues) {
    try {
      const values = await Promise.race([
        ua.getHighEntropyValues(['platformVersion']),
        new Promise<undefined>((resolve) => setTimeout(resolve, DETAILS_WAIT_MS)),
      ]);
      const version = values?.platformVersion
        ? readableOsVersion(ua.platform, values.platformVersion)
        : undefined;
      if (version) details.osVersion = version;
    } catch {
      /* sem a versão do sistema: só o nome dele aparece */
    }
  }
  return details;
}

let detailsRead: Promise<ClientDetails> | null = null;

/** Detalhes deste computador, lidos uma vez por abertura do painel. */
export function deviceDetails(): Promise<ClientDetails> {
  if (!detailsRead) detailsRead = readDetails();
  return detailsRead;
}

/** Identificador deste computador (cria na primeira vez). Sem storage, vale só nesta aba. */
let memoryDevice: string | null = null;
export function getDeviceId(): string {
  const store = storage();
  try {
    const saved = store?.getItem(DEVICE_KEY);
    if (saved && /^[A-Za-z0-9_-]{16,128}$/.test(saved)) return saved;
    const created = randomId();
    store?.setItem(DEVICE_KEY, created);
    return created;
  } catch {
    if (!memoryDevice) memoryDevice = randomId();
    return memoryDevice;
  }
}

export function getWatchdogToken(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return storage()?.getItem(TOKEN_PREFIX + userId) ?? null;
  } catch {
    return null;
  }
}

export function setWatchdogToken(userId: string, token: string): void {
  try {
    storage()?.setItem(TOKEN_PREFIX + userId, token);
  } catch {
    /* sem storage: o computador pede autorização de novo na próxima abertura */
  }
}

export function clearWatchdogToken(userId: string): void {
  try {
    storage()?.removeItem(TOKEN_PREFIX + userId);
  } catch {
    /* nada a limpar */
  }
}

/** Cabeçalhos das rotas `api/v2/`: o computador e, se houver, a sessão dele. */
export function watchdogHeaders(path: string): Record<string, string> {
  if (!path.replace(/^\/+/, '').startsWith('api/v2/')) return {};
  const headers: Record<string, string> = { 'watchdog-device': getDeviceId() };
  const token = getWatchdogToken(readAuth()?.userId);
  if (token) headers['watchdog-token'] = token;
  return headers;
}
